import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Input,
  Label,
  Badge,
} from "@asm/ui";
import {
  Radio,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Server,
  Wifi,
  Clock,
  Activity,
  Shield,
} from "lucide-react";
import { Link } from "react-router-dom";
import { lookupApi, type PortInfo } from "@/lib/api";

// Port state badge component
function PortStateBadge({ state }: { state: string }) {
  const stateConfig: Record<string, { className: string; label: string }> = {
    open: {
      className: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
      label: "OPEN",
    },
    closed: {
      className: "border-red-500/30 text-red-400 bg-red-500/10",
      label: "CLOSED",
    },
    filtered: {
      className: "border-amber-500/30 text-amber-400 bg-amber-500/10",
      label: "FILTERED",
    },
    open_filtered: {
      className: "border-cyan-500/30 text-cyan-400 bg-cyan-500/10",
      label: "OPEN|FILTERED",
    },
  };

  const config = stateConfig[state] || stateConfig.filtered;

  return (
    <Badge variant="outline" className={`font-mono text-[10px] ${config.className}`}>
      {config.label}
    </Badge>
  );
}

// Port row component
function PortRow({ port, index }: { port: PortInfo; index: number }) {
  return (
    <tr
      className="border-b border-border/50 hover:bg-muted/30 transition-colors animate-fade-in-up"
      style={{ animationDelay: `${index * 30}ms` }}
    >
      <td className="py-3 px-4 font-mono text-cyan-400">
        {port.port}
      </td>
      <td className="py-3 px-4 uppercase text-xs text-muted-foreground">
        {port.protocol}
      </td>
      <td className="py-3 px-4">
        <PortStateBadge state={port.state} />
      </td>
      <td className="py-3 px-4 text-sm">
        {port.service_hint || (
          <span className="text-muted-foreground italic">unknown</span>
        )}
      </td>
      <td className="py-3 px-4 text-right font-mono text-xs text-muted-foreground">
        {port.response_time_ms}ms
      </td>
    </tr>
  );
}

// Statistics card
function StatCard({
  label,
  value,
  icon: Icon,
  variant = "default",
}: {
  label: string;
  value: string | number;
  icon: React.ElementType;
  variant?: "default" | "cyan" | "emerald" | "amber";
}) {
  const variantStyles = {
    default: "border-border bg-card/50",
    cyan: "border-cyan-500/20 bg-cyan-500/5",
    emerald: "border-emerald-500/20 bg-emerald-500/5",
    amber: "border-amber-500/20 bg-amber-500/5",
  };

  const iconStyles = {
    default: "text-muted-foreground",
    cyan: "text-cyan-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
  };

  const valueStyles = {
    default: "text-foreground",
    cyan: "text-cyan-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
  };

  return (
    <div className={`rounded-lg border p-4 ${variantStyles[variant]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${iconStyles[variant]}`} />
        <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-2xl font-bold font-mono ${valueStyles[variant]}`}>
        {value}
      </div>
    </div>
  );
}

export default function PortScan() {
  const [target, setTarget] = useState("");

  const scanMutation = useMutation({
    mutationFn: (target: string) => lookupApi.portscan(target),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (target.trim()) {
      scanMutation.mutate(target.trim());
    }
  };

  const result = scanMutation.data;

  return (
    <div className="tactical-grid space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-center gap-4">
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <Radio className="h-8 w-8 text-amber-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Port Scanner</h1>
            <p className="text-muted-foreground">
              Scan TCP/UDP ports and detect running services
            </p>
          </div>
        </div>

        {/* Authorization notice */}
        <div className="mt-4 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
          <Shield className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <span className="text-amber-400 font-medium">Authorization Required</span>
            <p className="text-muted-foreground mt-1">
              Port scanning is an active reconnaissance technique. Only scan targets you own
              or have explicit authorization to test.
            </p>
          </div>
        </div>
      </div>

      {/* Scan Form */}
      <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-6 w-1 rounded-full bg-amber-500" />
          <div>
            <h2 className="text-lg font-semibold">Target Configuration</h2>
            <p className="text-xs text-muted-foreground">
              Enter an IP address or hostname to scan
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {scanMutation.error && (
            <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {scanMutation.error instanceof Error
                ? scanMutation.error.message
                : "Scan failed"}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="target" className="text-muted-foreground">
              Target
            </Label>
            <Input
              id="target"
              type="text"
              placeholder="e.g., 192.168.1.1 or example.com"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={scanMutation.isPending}
              className="bg-muted/50 border-border font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Scans Top 100 TCP ports and common UDP ports (DNS, NTP, SNMP)
            </p>
          </div>

          <Button
            type="submit"
            disabled={scanMutation.isPending || !target.trim()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {scanMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Radio className="mr-2 h-4 w-4" />
                Start Port Scan
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-6 animate-fade-in-up">
          {/* Summary */}
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <div>
              <span className="text-emerald-400 font-medium">Scan Complete</span>
              <span className="text-muted-foreground ml-2">
                {result.target} ({result.ip || "unresolved"})
              </span>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="TCP Open"
              value={result.stats.tcp_open}
              icon={Server}
              variant="emerald"
            />
            <StatCard
              label="UDP Open"
              value={result.stats.udp_open}
              icon={Wifi}
              variant="cyan"
            />
            <StatCard
              label="Filtered"
              value={result.stats.tcp_filtered + result.stats.udp_filtered}
              icon={Shield}
              variant="amber"
            />
            <StatCard
              label="Scan Time"
              value={`${result.scan_duration_ms}ms`}
              icon={Clock}
              variant="default"
            />
          </div>

          {/* TCP Ports Table */}
          {result.tcp_ports.length > 0 && (
            <div className="rounded-lg border border-border bg-card/50 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center gap-3">
                <Server className="h-5 w-5 text-cyan-400" />
                <div>
                  <h3 className="font-semibold">TCP Ports</h3>
                  <p className="text-xs text-muted-foreground">
                    {result.tcp_ports.length} port{result.tcp_ports.length !== 1 && "s"} discovered
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                      <th className="py-3 px-4">Port</th>
                      <th className="py-3 px-4">Protocol</th>
                      <th className="py-3 px-4">State</th>
                      <th className="py-3 px-4">Service</th>
                      <th className="py-3 px-4 text-right">Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.tcp_ports.map((port, index) => (
                      <PortRow key={`tcp-${port.port}`} port={port} index={index} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* UDP Ports Table */}
          {result.udp_ports.length > 0 && (
            <div className="rounded-lg border border-border bg-card/50 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center gap-3">
                <Wifi className="h-5 w-5 text-amber-400" />
                <div>
                  <h3 className="font-semibold">UDP Ports</h3>
                  <p className="text-xs text-muted-foreground">
                    {result.udp_ports.length} port{result.udp_ports.length !== 1 && "s"} discovered
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                      <th className="py-3 px-4">Port</th>
                      <th className="py-3 px-4">Protocol</th>
                      <th className="py-3 px-4">State</th>
                      <th className="py-3 px-4">Service</th>
                      <th className="py-3 px-4 text-right">Response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.udp_ports.map((port, index) => (
                      <PortRow key={`udp-${port.port}`} port={port} index={index} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* No ports found */}
          {result.tcp_ports.length === 0 && result.udp_ports.length === 0 && (
            <div className="rounded-lg border border-border bg-card/50 p-8 text-center">
              <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Open Ports Found</h3>
              <p className="text-muted-foreground text-sm">
                All scanned ports appear to be closed or filtered on this target.
              </p>
            </div>
          )}

          {/* Filtered ports summary */}
          {result.filtered_ports && result.filtered_ports.length > 0 && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-amber-400" />
                <span className="text-sm font-medium text-amber-400">Filtered Ports</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {result.filtered_ports.length} port{result.filtered_ports.length !== 1 && "s"} were
                filtered (firewall may be blocking probes)
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
